import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CHECK_INTERVAL = 30000; // Check every 30 seconds
const VERSION_STORAGE_KEY = 'c30_app_version';

/**
 * Hook to detect new app deployments and prompt users to refresh
 * This prevents data loss when users are working on stale cached versions
 */
export const useVersionCheck = () => {
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  const initialVersionRef = useRef(null);
  const isCheckingRef = useRef(false);

  // Fetch current version from server
  const fetchVersion = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/version`, {
        timeout: 5000,
        headers: { 'Cache-Control': 'no-cache' }
      });
      return response.data.version;
    } catch (error) {
      console.log('Version check failed:', error.message);
      return null;
    }
  }, []);

  // Check for new version
  const checkVersion = useCallback(async () => {
    // Prevent concurrent checks
    if (isCheckingRef.current) return;
    isCheckingRef.current = true;

    try {
      const serverVersion = await fetchVersion();
      if (!serverVersion) {
        isCheckingRef.current = false;
        return;
      }

      // Get the version we started with (from when page first loaded)
      const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);

      if (!storedVersion) {
        // First time ever - store the current version
        localStorage.setItem(VERSION_STORAGE_KEY, serverVersion);
        initialVersionRef.current = serverVersion;
        console.log('Version initialized:', serverVersion);
      } else if (!initialVersionRef.current) {
        // Page just loaded, set our reference to the stored version
        initialVersionRef.current = storedVersion;
        
        // Immediately check if server has a different version than what we stored
        if (serverVersion !== storedVersion) {
          console.log('New version detected on load!', { stored: storedVersion, server: serverVersion });
          setNewVersionAvailable(true);
        }
      } else {
        // Ongoing check - compare server version with what we had when page loaded
        if (serverVersion !== initialVersionRef.current) {
          console.log('New version detected!', { initial: initialVersionRef.current, server: serverVersion });
          setNewVersionAvailable(true);
        }
      }
    } finally {
      isCheckingRef.current = false;
    }
  }, [fetchVersion]);

  // Force refresh to home page
  const forceRefresh = useCallback(async () => {
    try {
      // First, get the NEW version from server and store it
      // This prevents the modal from appearing again after refresh
      const newVersion = await fetchVersion();
      if (newVersion) {
        localStorage.setItem(VERSION_STORAGE_KEY, newVersion);
        console.log('Updated stored version to:', newVersion);
      }
    } catch (e) {
      console.error('Error updating version before refresh:', e);
    }
    
    // Small delay to ensure localStorage is written
    setTimeout(() => {
      // Hard refresh to home page (bypass cache)
      window.location.href = '/' + '?v=' + Date.now();
    }, 100);
  }, [fetchVersion]);

  // Dismiss the notification (user chooses to continue without refreshing)
  const dismissNotification = useCallback(async () => {
    // Update stored version to current server version so we don't keep showing
    const newVersion = await fetchVersion();
    if (newVersion) {
      localStorage.setItem(VERSION_STORAGE_KEY, newVersion);
      initialVersionRef.current = newVersion;
    }
    setNewVersionAvailable(false);
  }, [fetchVersion]);

  // Initialize and start checking
  useEffect(() => {
    // Check immediately on mount
    checkVersion();

    // Set up periodic checking
    const interval = setInterval(checkVersion, CHECK_INTERVAL);

    // Also check when tab becomes visible again (user switches back to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Check when window gets focus
    const handleFocus = () => {
      checkVersion();
    };
    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [checkVersion]);

  return {
    newVersionAvailable,
    forceRefresh,
    dismissNotification
  };
};

export default useVersionCheck;
